import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Checkbox, message, Select, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import withRoleAccess from '../../hoc/withRoleAccess';

const { Option } = Select;

const SubjectModal = ({ visible, onClose, onSave, subjectData, subjectGroups = [] }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [selectedTitle, setSelectedTitle] = useState('');

  // Education level constants
const EDUCATION_LEVELS = {
  // US Grades only
  US_GRADES: Array.from({ length: 12 }, (_, i) => ({
    key: `us-grade-${i + 1}`,
    value: `G.${i + 1}`,
    label: `G.${i + 1}`
  })),

  // UK Years only
  UK_YEARS: Array.from({ length: 13 }, (_, i) => ({
    key: `uk-year-${i + 1}`,
    value: `Y.${i + 1}`,
    label: `Y.${i + 1}`
  })),

  // Combined Y./G. levels (Y.1/G.1 … Y.13/G.13)
  COMBINED_LEVELS: Array.from({ length: 12 }, (_, i) => ({
    key: `combined-${i + 1}`,
    value: `Y.${i + 1}/G.${i + 1}`,
    label: `Y.${i + 1}/G.${i + 1}`
  })),

  // New range levels
RANGE_LEVELS: [
  { key: 'range-y10-11', value: 'Y.10–11', label: 'Y.10–11' },
  { key: 'range-y12-13', value: 'Y.12–13', label: 'Y.12–13' }
],

  // General levels
  GENERAL_LEVELS: [
    { key: 'beginner', value: 'Beginner', label: 'Beginner' },
    { key: 'intermediate', value: 'Intermediate', label: 'Intermediate' },
    { key: 'advanced', value: 'Advanced', label: 'Advanced' },
    { key: 'college', value: 'College', label: 'College' },
    { key: 'general', value: 'General', label: 'General' }
  ]
};


  // Title options grouped by category
  const TITLE_CATEGORIES = {
    UK_CURRICULUM: [
      'KS1',
      'KS2', 
      'KS3',
      'IGCSE',
      'A-Level',
      'CAT4',
      'UKiSet',
      'ISEB P',
      'ISEB C',
      'Checkpoint 1',
      'Checkpoint 2',
      'Checkpoint 3'
    ],
    US_CURRICULUM: [
      'Improve School Grades',
      'GED',
      'PSAT',
      'SAT',
      'SSAT',
      'AP',
      'Pre-AP',
      'ACT',
      'MAP Test',
      '11+',
      '13+',
      'Math Olympiad'
    ],
    UNIVERSITY_ENTRANCE: [
      'KMIDS Admission',
      'MUIDS Admission',
      'SPIP Admission',
      'PCU-ATS',
      'CU-AAT',
      'GMAT',
      'GRE'
    ],
    ENGLISH_PROFICIENCY: [
      'IELTS',
      'TOEFL Pri',
      'TOEFL Jr',
      'TOEFL ITP',
      'TOEFL IiBT',
      'Essay Writing',
      'English Literacy',
      'Conversation',
      'Business English',
      'Thai Language'
    ],
    IB_CURRICULUM: [
      'IB PYP',
      'IB MYP',
      'IB Diploma'
    ]
  };

  // General specifications (for most titles)
  const GENERAL_SPECIFICATIONS = [
    'English',
    'Math',
    'Science',
    'Verbal',
    'Non-Verbal',
    'Quantitative',
    'Integrated Reasoning',
    'Analytical Writing',
    'Biology',
    'Physics',
    'Chemistry',
    'Further Math',
    'Pure Math',
    'Calculus',
    'Business Studies',
    'Economics',
    'EFL',
    'ESL',
    'History',
    'Geography',
    'Social Studies',
    'RLA',
    'Essay Writing',
    'Thai Language',
    'Speaking',
    'Listening',
    'Writing',
    'Reading',
    'Interview',
    'Lan Form & Meaning',
    'Structure & Written Ex.',
    'All Skills',
    'TOEFL Primary',
    'TOEFL Jr.',
    'TOEFL ITP',
    'TOEFL IiBT',
    'Conversation',
    'Business English',
  ];

  // Specific specifications for English proficiency tests
  const ENGLISH_PROFICIENCY_SPECIFICATIONS = [
    'Speaking',
    'Listening',
    'Writing',
    'Reading',
    'Interview',
    'Lan Form & Meaning',
    'Structure & Written Ex.',
    'All Skills'
  ];

  // English proficiency titles that use different specifications
  const ENGLISH_PROFICIENCY_TITLES = [
    'IELTS',
    'TOEFL Primary',
    'TOEFL Jr.',
    'TOEFL ITP',
    'TOEFL IiBT',
    'Essay Writing',
    'Conversation',
    'Business English',
    'Thai Language'
  ];

  // Get appropriate specifications based on selected title
  const getSpecificationOptions = () => {
    if (ENGLISH_PROFICIENCY_TITLES.includes(selectedTitle)) {
      return ENGLISH_PROFICIENCY_SPECIFICATIONS;
    }
    return GENERAL_SPECIFICATIONS;
  };

useEffect(() => {
  if (visible) {
    if (subjectData) {
      // Parse existing title if it contains specification and/or level
      let parsedTitle = subjectData.title || '';
      let parsedSpecification = subjectData.specification || '';
      let parsedLevel = subjectData.level || '';
      
      // Extract level from title if it's in parentheses at the end
      const levelMatch = parsedTitle.match(/\s*\(([^)]+)\)$/);
      if (levelMatch && !parsedLevel) {
        parsedLevel = levelMatch[1];
        parsedTitle = parsedTitle.replace(/\s*\([^)]+\)$/, ''); // Remove level from title
      }
      
      // If title contains " - ", try to split it for specification
      if (parsedTitle.includes(' - ') && !parsedSpecification) {
        const parts = parsedTitle.split(' - ');
        if (parts.length === 2) {
          parsedTitle = parts[0];
          parsedSpecification = parts[1];
        }
      }
      
      form.setFieldsValue({
        ...subjectData,
        title: parsedTitle,
        specification: parsedSpecification,
        level: parsedLevel,
        language: Array.isArray(subjectData.language)
          ? subjectData.language
          : (typeof subjectData.language === 'string'
              ? JSON.parse(subjectData.language || '[]')
              : [])
      });
      
      // Set the selected title to show appropriate specifications
      setSelectedTitle(parsedTitle);
    } else {
      form.resetFields();
      setSelectedTitle('');
    }
  }
}, [visible, subjectData, form]);

  const handleTitleChange = (value) => {
    setSelectedTitle(value);
    // Clear specification field when title changes
    form.setFieldsValue({ specification: undefined });
  };

const handleSubmit = async () => {
  try {
    const values = await form.validateFields();

    // Combine title and specification into one field
    if (values.title && values.specification) {
      values.title = `${values.title} - ${values.specification}`;
    }

    // Append level in parentheses ONLY if it starts with Y. or G.
    if (values.title && values.level && /^Y\.|^G\./.test(values.level)) {
      values.title = `${values.title} (${values.level})`;
    }

    // Remove specification from saved data since it's now merged
    delete values.specification;

    // Map group ID to group name
    if (values.subjectgroupid) {
      const selectedGroup = subjectGroups.find(group => group.id === values.subjectgroupid);
      if (selectedGroup) {
        values.subjectgroup = selectedGroup.name;
      }
    }

    await onSave(values);
    message.success(t('changesSaved'));
    onClose();
  } catch (error) {
    console.error('Validation failed:', error);
    message.error(t('saveSubjectError'));
  }
};


  return (
    <Modal
      open={visible}
      title={subjectData ? t('editSubject') : t('createSubject')}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={t('save')}
      width={700}
    >
      <Form form={form} layout="vertical">
        {/* Subject Group Dropdown */}
        <Form.Item
          name="subjectgroupid"
          label={t('subjectGroup')}
          rules={[{ required: true, message: t('fieldRequired') }]}
        >
          <Select 
            placeholder={t('selectSubjectGroup')}
            showSearch
            allowClear
            filterOption={(input, option) => {
              if (option.value) {
                return option.children.toLowerCase().includes(input.toLowerCase());
              }
              return false;
            }}
          >
            {subjectGroups.map((group) => (
              <Option key={group.id} value={group.id}>
                {group.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        
        {/* Legacy Subject Group Input (hidden) */}
        <Form.Item name="subjectgroup" hidden>
          <Input />
        </Form.Item>
        
        {/* Level Dropdown with Fixed Options + Additional Levels */}
        <Form.Item
          name="level"
          label={t('level')}
          rules={[{ required: true, message: t('fieldRequired') }]}
        >
          <Select 
            placeholder={t('selectLevel')}
            showSearch
            allowClear
            filterOption={(input, option) => {
              if (option.value) {
                return option.value.toLowerCase().includes(input.toLowerCase());
              }
              return false;
            }}
          >
            <Select.OptGroup label="US Grades">
              {EDUCATION_LEVELS.US_GRADES.map(level => (
                <Option key={level.key} value={level.value}>
                  {level.label}
                </Option>
              ))}
            </Select.OptGroup>
            
            <Select.OptGroup label="UK Years">
              {EDUCATION_LEVELS.UK_YEARS.map(level => (
                <Option key={level.key} value={level.value}>
                  {level.label}
                </Option>
              ))}
            </Select.OptGroup>

            <Select.OptGroup label="Combined Levels">
              {EDUCATION_LEVELS.COMBINED_LEVELS.map(level => (
                <Option key={level.key} value={level.value}>
                  {level.label}
                </Option>
              ))}
            </Select.OptGroup>

            <Select.OptGroup label="Year Ranges">
              {EDUCATION_LEVELS.RANGE_LEVELS.map(level => (
                <Option key={level.key} value={level.value}>
                  {level.label}
                </Option>
              ))}
            </Select.OptGroup>

            
            <Select.OptGroup label="General Levels">
              {EDUCATION_LEVELS.GENERAL_LEVELS.map(level => (
                <Option key={level.key} value={level.value}>
                  {level.label}
                </Option>
              ))}
            </Select.OptGroup>
          </Select>
        </Form.Item>

        {/* Title Dropdown with Categories + Custom Input */}
        <Form.Item
          name="title"
          label={t('subjecttitle')}
          rules={[{ required: true, message: t('fieldRequired') }]}
        >
          <Select 
            placeholder={t('selectOrTypeSubjectTitle')}
            showSearch
            allowClear
            onChange={handleTitleChange}
            showArrow={true}
            filterOption={(input, option) => {
              if (option && option.children) {
                return option.children.toLowerCase().includes(input.toLowerCase());
              }
              return false;
            }}
            onSearch={(value) => {
              // Allow typing by setting the form value directly
              if (value && !TITLE_CATEGORIES.UK_CURRICULUM.includes(value) && 
                  !TITLE_CATEGORIES.US_CURRICULUM.includes(value) &&
                  !TITLE_CATEGORIES.UNIVERSITY_ENTRANCE.includes(value) &&
                  !TITLE_CATEGORIES.ENGLISH_PROFICIENCY.includes(value) &&
                  !TITLE_CATEGORIES.IB_CURRICULUM.includes(value)) {
                // This is a custom value being typed
                setTimeout(() => {
                  form.setFieldsValue({ title: value });
                  setSelectedTitle(value);
                }, 0);
              }
            }}
            onBlur={() => {
              // Ensure the typed value is saved when user clicks away
              const currentValue = form.getFieldValue('title');
              if (currentValue) {
                setSelectedTitle(currentValue);
              }
            }}
            dropdownRender={(menu) => (
              <div>
                {menu}
                <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0', fontSize: '12px', color: '#666' }}>
                  💡 Tip: You can select from the list above or type your own subject title
                </div>
              </div>
            )}
          >
            <Select.OptGroup label="UK Curriculum">
              {TITLE_CATEGORIES.UK_CURRICULUM.map(title => (
                <Option key={`uk-${title}`} value={title}>
                  {title}
                </Option>
              ))}
            </Select.OptGroup>
            
            <Select.OptGroup label="US Curriculum">
              {TITLE_CATEGORIES.US_CURRICULUM.map(title => (
                <Option key={`us-${title}`} value={title}>
                  {title}
                </Option>
              ))}
            </Select.OptGroup>
            
            <Select.OptGroup label="University Entrance">
              {TITLE_CATEGORIES.UNIVERSITY_ENTRANCE.map(title => (
                <Option key={`uni-${title}`} value={title}>
                  {title}
                </Option>
              ))}
            </Select.OptGroup>
            
            <Select.OptGroup label="English Proficiency">
              {TITLE_CATEGORIES.ENGLISH_PROFICIENCY.map(title => (
                <Option key={`eng-${title}`} value={title}>
                  {title}
                </Option>
              ))}
            </Select.OptGroup>
            
            <Select.OptGroup label="IB Curriculum">
              {TITLE_CATEGORIES.IB_CURRICULUM.map(title => (
                <Option key={`ib-${title}`} value={title}>
                  {title}
                </Option>
              ))}
            </Select.OptGroup>
          </Select>
        </Form.Item>

        {/* Specification - Select Only (will be combined with title) */}
        <Form.Item
          name="specification"
          label={t('specification')}
          rules={[{ required: true, message: t('fieldRequired') }]}
        >
          <Select 
            placeholder={selectedTitle ? 
              (ENGLISH_PROFICIENCY_TITLES.includes(selectedTitle) 
                ? t('selectEnglishProficiencySpec') 
                : t('selectGeneralSpec'))
              : t('selectTitleFirst')
            }
            showSearch
            allowClear
            disabled={!selectedTitle}
            filterOption={(input, option) => {
              if (option && option.children) {
                return option.children.toLowerCase().includes(input.toLowerCase());
              }
              return false;
            }}
          >
            {getSpecificationOptions().map(spec => (
              <Option key={spec} value={spec}>
                {spec}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="description"
          label={
            <span>
              {t('description')} <span style={{ fontSize: '12px', color: '#999', fontWeight: 'normal' }}>(max 255 characters)</span>
            </span>
          }
          rules={[
            { required: true, message: t('fieldRequired') },
            { max: 255, message: t('descriptionTooLong') }
          ]}
        >
          <Input.TextArea 
            rows={3} 
            showCount 
            maxLength={255} 
          />
        </Form.Item>
        
        <Form.Item
          name="image_url"
          label={t('imageUrl')}
          rules={[{ required: false }]}
        >
          <Input />
        </Form.Item>
        
      </Form>
    </Modal>
  );
};

export default withRoleAccess(SubjectModal, ['admin', 'superadmin']);